import React from 'react';
import { Button, Col, Row } from 'antd';
import { nullSafeGet } from '../../utils/DataAccessUtils';

const style = require('./SimilarSuppliersPanel.less');

// Title for the modal/step hosting the panel. Shared so every flow phrases the question the same way.
export const getSimilarSuppliersTitle = (matchCount) =>
	matchCount > 1 ? 'Did you mean any of the following Suppliers?' : 'Did you mean this Supplier?';

interface SimilarSupplierRowProps {
	supplier: any;
	isAlreadyInNetwork: boolean;
	selectButtonText: string;
	onSelect: (supplier: any, isAlreadyInNetwork: boolean) => void;
}

const SimilarSupplierRow: React.FC<SimilarSupplierRowProps> = ({
	supplier,
	isAlreadyInNetwork,
	selectButtonText,
	onSelect,
}) => {
	const streetAddress = nullSafeGet('primaryAddress.streetAddress1', supplier);
	const cityAndRegion = [
		nullSafeGet('primaryAddress.city', supplier),
		nullSafeGet('primaryAddress.region', supplier),
	]
		.filter(Boolean)
		.join(', ');

	const handleSelect = () => {
		onSelect(supplier, isAlreadyInNetwork);
	};

	return (
		<Row className="similarSupplierRow" style={{ flexWrap: 'nowrap' }}>
			<Col className="similarSupplierLogoContainer">
				<div className="similarSupplierLogo" />
			</Col>
			<Col className="similarSupplierInfoContainer">
				<Row
					className="similarSupplierNameContainer"
					style={{ overflowX: 'hidden', overflowY: 'hidden' }}
				>
					{nullSafeGet('displayName', supplier) || nullSafeGet('name', supplier)}
				</Row>
				{streetAddress ? (
					<Row className="similarSupplierAddressLine" style={{ flexWrap: 'nowrap' }}>
						{streetAddress}
					</Row>
				) : null}
				{cityAndRegion ? (
					<Row className="similarSupplierAddressLine" style={{ flexWrap: 'nowrap' }}>
						{cityAndRegion}
					</Row>
				) : null}
				<Row className="similarSupplierTypeContainer">
					{isAlreadyInNetwork ? (
						<span className="footnoteregular text-character-danger">
							This supplier is currently in your private network
						</span>
					) : (
						nullSafeGet('type', supplier)
					)}
				</Row>
			</Col>
			<Col className="similarSupplierButtonContainer">
				<Button type="primary" onClick={handleSelect}>
					{selectButtonText}
				</Button>
			</Col>
		</Row>
	);
};

interface SimilarSuppliersPanelProps {
	/** Supplier facilities returned by the name + email duplicate lookup. */
	suppliers: any[];
	/** Ids (as strings) already in the buyer's private network — those rows are flagged and reported back. */
	existingSupplierIds?: string[];
	/** Optional copy above the list explaining what selecting a row does in this particular flow. */
	description?: React.ReactNode;
	selectButtonText?: string;
	/**
	 * `isAlreadyInNetwork` tells the caller whether the picked supplier was flagged via
	 * `existingSupplierIds` — the private network pages branch to their edit flow on it.
	 */
	onSelectSupplier: (supplier: any, isAlreadyInNetwork: boolean) => void;
	wrapperClassName?: string;
}

// The "Did you mean this Supplier?" list, shared by the private network add flows and by the
// Suggested Entities / approval flows.
const SimilarSuppliersPanel: React.FC<SimilarSuppliersPanelProps> = ({
	suppliers,
	existingSupplierIds = [],
	description,
	selectButtonText = 'Yes, this one',
	onSelectSupplier,
	wrapperClassName = 'max-h-[400px] overflow-y-auto',
}) => (
	<div className={wrapperClassName}>
		{description ? (
			<div className="bodyregular mb-3 text-character-secondary">{description}</div>
		) : null}
		{(suppliers || []).map((supplier, index) => (
			<SimilarSupplierRow
				key={nullSafeGet('id', supplier) || index}
				supplier={supplier}
				isAlreadyInNetwork={existingSupplierIds.includes(String(nullSafeGet('id', supplier)))}
				selectButtonText={selectButtonText}
				onSelect={onSelectSupplier}
			/>
		))}
	</div>
);

export default SimilarSuppliersPanel;
